import { usePageRefresh } from '../../../context/PageRefreshContext';
import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getTasksList, transitionTask, getProjectsList } from '../../../redux/actions/SocietyPortal/TaskAction/TaskAction';
import usePermissions from '../../../hook/usePermissions';
import { PERMISSIONS } from '../../../consts/permissions';
import { notify } from '../../../utils/notification';
import {
    TASK_STATUSES, TASK_PRIORITIES, TASK_DEPARTMENTS,
    STATUS_BADGE, priorityBadgeClass, humanize,
    getAllowedTransitions, getTransitionPermission,
} from '../../../consts/taskConstants';

const formatDate = (value) => {
    if (!value) return null;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
};

const isOverdue = (task) =>
    task.due_date && !['COMPLETED', 'CLOSED', 'CANCELLED'].includes(task.status) &&
    new Date(task.due_date).getTime() < Date.now();

const initials = (person) => {
    if (!person) return '';
    const f = person.first_name || '';
    const l = person.last_name || '';
    return `${f.charAt(0)}${l.charAt(0)}`.toUpperCase() || (person.email || '?').charAt(0).toUpperCase();
};

// Accent colour per status column header — derived from the shared badge palette.
const columnAccent = (status) => STATUS_BADGE[status] || 'tw-bg-gray-100 tw-text-gray-600';

const Board = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { can } = usePermissions();

    const { tasksListData, tasksListLoading, transitionTaskLoading, projectsListData } = useSelector((s) => s.TaskReducer);

    const [searchQuery, setSearchQuery] = useState('');
    const [filterPriority, setFilterPriority] = useState('');
    const [filterDepartment, setFilterDepartment] = useState('');
    const [filterProject, setFilterProject] = useState('');
    // Local copy so a drag drop can update the UI optimistically before the server confirms.
    const [board, setBoard] = useState([]);
    const [dragOverStatus, setDragOverStatus] = useState('');
    const draggedIdRef = useRef(null);
    const prevTransitioning = useRef(false);

    const fetchTasks = useCallback(() => {
        dispatch(getTasksList({
            page: 1,
            limit: 500,
            search: searchQuery || undefined,
            priority: filterPriority || undefined,
            department: filterDepartment || undefined,
            project_id: filterProject || undefined,
            sort_by: 'created_at',
            sort_dir: 'desc',
        }));
    }, [dispatch, searchQuery, filterPriority, filterDepartment, filterProject]);
    usePageRefresh(fetchTasks);

    useEffect(() => {
        fetchTasks();
    }, [fetchTasks]);

    useEffect(() => {
        dispatch(getProjectsList({}));
    }, [dispatch]);

    const projects = Array.isArray(projectsListData) ? projectsListData : [];

    useEffect(() => {
        setBoard(Array.isArray(tasksListData) ? tasksListData : []);
    }, [tasksListData]);

    // Reconcile with the server after every transition (covers both success and failure,
    // so an optimistic move that the backend rejects snaps back to the true status).
    useEffect(() => {
        if (prevTransitioning.current && !transitionTaskLoading) fetchTasks();
        prevTransitioning.current = transitionTaskLoading;
    }, [transitionTaskLoading, fetchTasks]);

    const columns = useMemo(() => {
        const grouped = TASK_STATUSES.reduce((acc, s) => ({ ...acc, [s]: [] }), {});
        board.forEach((t) => {
            if (grouped[t.status]) grouped[t.status].push(t);
            else grouped[t.status] = [t];
        });
        return TASK_STATUSES.map((status) => ({ status, tasks: grouped[status] || [] }));
    }, [board]);

    const hasActiveFilters = searchQuery || filterPriority || filterDepartment || filterProject;
    const clearFilters = () => { setSearchQuery(''); setFilterPriority(''); setFilterDepartment(''); setFilterProject(''); };

    const handleDrop = (targetStatus) => {
        setDragOverStatus('');
        const taskId = draggedIdRef.current;
        draggedIdRef.current = null;
        if (!taskId) return;

        const task = board.find((t) => t.task_id === taskId);
        if (!task || task.status === targetStatus) return;

        if (!getAllowedTransitions(task.status).includes(targetStatus)) {
            notify('warning', `Can't move ${humanize(task.status)} → ${humanize(targetStatus)}`);
            return;
        }
        if (!can(getTransitionPermission(targetStatus))) {
            notify('warning', 'You do not have permission for this transition');
            return;
        }

        // Optimistic move; the reconcile effect will confirm/revert against the server.
        setBoard((prev) => prev.map((t) => (t.task_id === taskId ? { ...t, status: targetStatus } : t)));
        dispatch(transitionTask({ taskId, status: targetStatus }));
    };

    return (
        <>
            {/* Header */}
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
                <div className="tw-flex tw-items-center tw-gap-2">
                    <button onClick={() => navigate('/society-portal/tasks')} className="tw-p-1.5 tw-rounded hover:tw-bg-gray-100 tw-text-gray-500">
                        <Icon icon="mdi:arrow-left" />
                    </button>
                    <div>
                        <h1 className="tw-text-lg tw-font-semibold tw-text-gray-800">Board</h1>
                        <p className="tw-text-xs tw-text-gray-500">Drag tasks across columns to update their status</p>
                    </div>
                </div>
                <div className="tw-flex tw-items-center tw-gap-2">
                    {/* List / Board toggle */}
                    <div className="tw-flex tw-items-center tw-bg-white tw-border tw-border-gray-200 tw-rounded-md tw-overflow-hidden">
                        <button onClick={() => navigate('/society-portal/tasks')} className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-sm tw-text-gray-600 hover:tw-bg-gray-50">
                            <Icon icon="mdi:format-list-bulleted" /> List
                        </button>
                        <button className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-bg-blue-50 tw-text-blue-700">
                            <Icon icon="mdi:view-column-outline" /> Board
                        </button>
                    </div>
                    {can(PERMISSIONS.TASK_CREATE) && (
                        <button onClick={() => navigate('/society-portal/tasks/add')} className="tw-flex tw-items-center tw-gap-1.5 tw-bg-blue-600 tw-text-white tw-px-3 tw-py-1.5 tw-rounded-md tw-text-sm tw-font-medium hover:tw-bg-blue-700">
                            <Icon icon="mdi:plus" /> New Task
                        </button>
                    )}
                </div>
            </div>

            {/* Filter Bar */}
            <div className="tw-bg-white tw-rounded-lg tw-py-2.5 tw-px-3 tw-mb-4 tw-shadow-sm tw-border tw-border-gray-100 tw-flex tw-items-center tw-gap-2.5 tw-flex-wrap">
                <div className="tw-flex-1 tw-min-w-[200px] tw-relative">
                    <Icon icon="mdi:magnify" className="tw-absolute tw-left-2.5 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-400 tw-text-sm" />
                    <input type="text" placeholder="Search by title, number or description..." value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="tw-w-full tw-pl-8 tw-pr-2 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm focus:tw-outline-none focus:tw-border-blue-500" />
                </div>
                <select value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)}
                    className="tw-px-2.5 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm tw-bg-white focus:tw-outline-none focus:tw-border-blue-500">
                    <option value="">All Priority</option>
                    {TASK_PRIORITIES.map((p) => <option key={p} value={p}>{humanize(p)}</option>)}
                </select>
                <select value={filterDepartment} onChange={(e) => setFilterDepartment(e.target.value)}
                    className="tw-px-2.5 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm tw-bg-white focus:tw-outline-none focus:tw-border-blue-500">
                    <option value="">All Departments</option>
                    {TASK_DEPARTMENTS.map((d) => <option key={d} value={d}>{humanize(d)}</option>)}
                </select>
                <select value={filterProject} onChange={(e) => setFilterProject(e.target.value)}
                    className="tw-px-2.5 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm tw-bg-white focus:tw-outline-none focus:tw-border-blue-500">
                    <option value="">All Projects</option>
                    {projects.map((p) => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
                </select>
                {hasActiveFilters && (
                    <button onClick={clearFilters} className="tw-flex tw-items-center tw-gap-1 tw-text-red-500 tw-text-xs tw-font-medium hover:tw-text-red-600">
                        <Icon icon="mdi:close" className="tw-text-sm" /> Clear
                    </button>
                )}
            </div>

            {/* Loading */}
            {tasksListLoading && board.length === 0 ? (
                <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-text-center tw-py-12">
                    <Icon icon="mdi:loading" className="tw-text-4xl tw-text-blue-500 tw-animate-spin" />
                    <p className="tw-text-gray-500 tw-mt-2 tw-text-sm">Loading board...</p>
                </div>
            ) : (
                /* Columns */
                <div className="tw-flex tw-gap-3 tw-overflow-x-auto tw-pb-3">
                    {columns.map(({ status, tasks }) => (
                        <div
                            key={status}
                            onDragOver={(e) => { e.preventDefault(); setDragOverStatus(status); }}
                            onDragLeave={() => setDragOverStatus((s) => (s === status ? '' : s))}
                            onDrop={() => handleDrop(status)}
                            className={`tw-flex-shrink-0 tw-w-[280px] tw-bg-gray-50 tw-rounded-lg tw-border tw-transition-colors ${
                                dragOverStatus === status ? 'tw-border-blue-400 tw-bg-blue-50' : 'tw-border-gray-100'
                            }`}
                        >
                            {/* Column header */}
                            <div className="tw-flex tw-items-center tw-justify-between tw-px-3 tw-py-2.5 tw-border-b tw-border-gray-100 tw-sticky tw-top-0">
                                <span className={`tw-px-2 tw-py-0.5 tw-rounded tw-text-[11px] tw-font-semibold ${columnAccent(status)}`}>
                                    {humanize(status)}
                                </span>
                                <span className="tw-text-xs tw-font-medium tw-text-gray-400">{tasks.length}</span>
                            </div>

                            {/* Cards */}
                            <div className="tw-p-2 tw-space-y-2 tw-min-h-[120px] tw-max-h-[calc(100vh-260px)] tw-overflow-y-auto">
                                {tasks.map((task) => (
                                    <div
                                        key={task.task_id}
                                        draggable
                                        onDragStart={() => { draggedIdRef.current = task.task_id; }}
                                        onDragEnd={() => { draggedIdRef.current = null; setDragOverStatus(''); }}
                                        onClick={() => navigate(`/society-portal/tasks/view/${task.task_id}`)}
                                        className="tw-bg-white tw-rounded-md tw-border tw-border-gray-200 tw-p-2.5 tw-shadow-sm tw-cursor-pointer hover:tw-border-blue-300 hover:tw-shadow tw-transition"
                                    >
                                        <p className="tw-text-sm tw-font-medium tw-text-gray-800 tw-line-clamp-2 tw-mb-2">{task.title}</p>
                                        <div className="tw-flex tw-items-center tw-gap-1.5 tw-flex-wrap tw-mb-2">
                                            <span className={`tw-px-1.5 tw-py-0.5 tw-rounded tw-text-[10px] tw-font-medium ${priorityBadgeClass(task.priority)}`}>
                                                {humanize(task.priority)}
                                            </span>
                                            {task.project?.name && (
                                                <span className="tw-flex tw-items-center tw-gap-0.5 tw-px-1.5 tw-py-0.5 tw-rounded tw-text-[10px] tw-font-medium tw-bg-indigo-50 tw-text-indigo-700">
                                                    <Icon icon="hugeicons:folder-01" /> {task.project.name}
                                                </span>
                                            )}
                                            {task.department && (
                                                <span className="tw-px-1.5 tw-py-0.5 tw-rounded tw-text-[10px] tw-font-medium tw-bg-gray-100 tw-text-gray-600">
                                                    {humanize(task.department)}
                                                </span>
                                            )}
                                        </div>
                                        <div className="tw-flex tw-items-center tw-justify-between">
                                            <span className="tw-text-[10px] tw-text-gray-400 tw-font-mono">{task.task_number}</span>
                                            <div className="tw-flex tw-items-center tw-gap-2">
                                                {task.due_date && (
                                                    <span className={`tw-flex tw-items-center tw-gap-0.5 tw-text-[10px] ${isOverdue(task) ? 'tw-text-red-600 tw-font-medium' : 'tw-text-gray-500'}`}>
                                                        <Icon icon="mdi:calendar-outline" /> {formatDate(task.due_date)}
                                                    </span>
                                                )}
                                                {task.assignee && (
                                                    <span title={`${task.assignee.first_name || ''} ${task.assignee.last_name || ''}`.trim()}
                                                        className="tw-w-6 tw-h-6 tw-rounded-full tw-bg-blue-100 tw-text-blue-700 tw-text-[10px] tw-font-semibold tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                                                        {initials(task.assignee)}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                {tasks.length === 0 && (
                                    <div className="tw-text-center tw-py-6 tw-text-[11px] tw-text-gray-300">No tasks</div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </>
    );
};

export default Board;
